import { motion } from "framer-motion";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { LOCATION_LEVELS } from "../../lib/materials";
import { MATERIALS_CARD } from "../../lib/product";
import { SectionIntro, DemoChip } from "./shared";

const MATERIAL_IMG =
    "https://images.unsplash.com/photo-1595414440701-da000c40df9c?q=80&w=1200&auto=format&fit=crop";

const LiveMaterialsIntelligence = () => (
    <section
        id="materials"
        data-testid="live-materials-section"
        className="relative bg-surface/40 py-24 lg:py-32"
    >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <SectionIntro
                testid="materials-intro"
                eyebrow="LIVE MATERIALS INTELLIGENCE"
                title="We don't guess prices. We verify them."
            />

            <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-16">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                >
                    <div className="mb-4 flex items-center justify-between font-mono text-[13px] tracking-[0.2em] text-cad">
                        <span>FIG. M.01 — VERIFIED MATERIAL PRICE</span>
                        <DemoChip>DEMO DATA</DemoChip>
                    </div>
                    <div
                        data-testid="materials-price-card"
                        className="border border-white/15 bg-[#0C1428]"
                    >
                        <div className="relative h-44 overflow-hidden">
                            <img
                                src={MATERIAL_IMG}
                                alt="Construction materials — cement, brick and timber"
                                loading="lazy"
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <div className="flex items-start justify-between border-b border-white/10 px-6 py-5">
                            <div>
                                <p className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                    PRODUCT
                                </p>
                                <h3 className="mt-2 font-display text-xl font-semibold text-white">
                                    {MATERIALS_CARD.product}
                                </h3>
                            </div>
                        </div>
                        {[
                            { label: "PROJECT LOCATION", value: MATERIALS_CARD.location },
                            { label: "SUPPLIER", value: MATERIALS_CARD.supplier },
                            { label: "PRICE", value: MATERIALS_CARD.price },
                        ].map((row) => (
                            <div
                                key={row.label}
                                className="flex items-center justify-between border-b border-white/[0.07] px-6 py-4"
                            >
                                <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                    {row.label}
                                </span>
                                <span className="text-[15px] text-white/90">
                                    {row.value}
                                </span>
                            </div>
                        ))}
                        <div className="flex items-center justify-between px-6 py-5">
                            <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                STATUS
                            </span>
                            <span
                                data-testid="materials-status"
                                className="flex items-center gap-2.5 font-mono text-[13px] tracking-[0.22em] text-emerald-400"
                            >
                                <motion.span
                                    animate={{ opacity: [1, 0.35, 1] }}
                                    transition={{
                                        duration: 2.2,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                    className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                                />
                                {MATERIALS_CARD.status}
                            </span>
                        </div>
                        <p className="border-t border-white/10 px-6 py-4 font-mono text-[13px] tracking-[0.2em] text-mist">
                            {MATERIALS_CARD.stamp} — NO PRICE SHOWN UNTIL
                            VERIFIED
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                >
                    <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                        LOCATION MATTERS
                    </p>
                    <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-mist">
                        Material prices are sourced in priority order, closest
                        to where you are building. A Johannesburg benchmark is
                        never presented as Durban pricing.
                    </p>
                    <ol
                        data-testid="materials-hierarchy"
                        className="mt-8 border-t border-white/10"
                    >
                        {LOCATION_LEVELS.map((level) => (
                            <li
                                key={level.n}
                                className="flex items-center justify-between border-b border-white/10 px-1 py-4"
                            >
                                <div className="flex items-center gap-4">
                                    <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                        {level.n}
                                    </span>
                                    <span
                                        className={`text-[15px] font-medium ${level.tag ? "text-white" : "text-mist"}`}
                                    >
                                        {level.label}
                                    </span>
                                </div>
                                {level.tag ? (
                                    <span className="bg-accent px-2 py-1 font-mono text-[13px] tracking-[0.22em] text-white">
                                        {level.tag}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ol>
                    <p className="mt-6 font-mono text-[13px] leading-relaxed tracking-[0.18em] text-cad">
                        PRIORITY: LOCAL SUPPLIER/BRANCH → CITY/METRO → PROVINCE
                        → NEARBY REGION → NATIONAL REFERENCE ONLY WHERE LOCAL
                        DATA ISN'T AVAILABLE
                    </p>
                </motion.div>
            </div>
        </div>
    </section>
);

export default LiveMaterialsIntelligence;
